import { Component, createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import type { IslandProps } from './registry'

/**
 * Wraps every mounted island with:
 *   - an error boundary (one bad island can't crash the whole React tree)
 *   - a SessionContext that reflects mount-time `user` plus live updates
 *     from `supabase.auth.onAuthStateChange`
 */

interface SessionState {
  user: IslandProps['user']
  status: 'authed' | 'anon' | 'loading'
}

const SessionContext = createContext<SessionState>({ user: null, status: 'loading' })

export function useSession(): SessionState {
  return useContext(SessionContext)
}

export function IslandRoot({
  user,
  children,
}: {
  user: IslandProps['user']
  children?: ReactNode
}) {
  const [session, setSession] = useState<SessionState>(() => ({
    user,
    status: user ? 'authed' : 'loading',
  }))

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === 'SIGNED_OUT' || !sess) {
        setSession({ user: null, status: 'anon' })
      } else if (sess.user) {
        const next: SessionState['user'] =
          sess.user.email !== undefined
            ? { id: sess.user.id, email: sess.user.email }
            : { id: sess.user.id }
        setSession({ user: next, status: 'authed' })
      }
    })
    return () => data.subscription.unsubscribe()
  }, [])

  return (
    <SessionContext.Provider value={session}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </SessionContext.Provider>
  )
}

interface ErrorBoundaryState {
  error: Error | null
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    console.error('Island crashed:', error, info.componentStack)
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div style={{ padding: 20, color: 'var(--red, #f87171)' }}>
          <strong>Island error.</strong> Reload the page to recover. Details in console.
        </div>
      )
    }
    return this.props.children
  }
}
